import { useState } from "react";
import { Save, FileDown, FileSpreadsheet, FileText, Printer, AlertTriangle, ChevronDown, ChevronUp, Sparkles, Pencil, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { QUARTERS, money, qty } from "@/lib/poshanConstants";
import { generatorStateApi } from "@/lib/setupApi";
import StickyActionBar from "@/components/mobile/StickyActionBar";

// Mobile-only Quarterly Reports / Generator layout. Visible at <md (768px).
// Tablets & desktop fall back to the existing layout. Keeps editing scope
// intentionally small: monthly meal + cooking-cost fields, validation,
// ingredient summary, and the three export actions.
function Stat({ label, value, valueClass = "text-ink-900" }) {
  return (
    <div className="flex-1 rounded-2xl border border-border bg-white p-3.5 shadow-[0_1px_0_rgba(44,53,40,0.04)]">
      <p className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">{label}</p>
      <p className={`mt-1 font-heading text-lg font-bold ${valueClass}`}>{value}</p>
    </div>
  );
}

function Accordion({ title, defaultOpen = false, badge, testId, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="mx-4 mt-4 overflow-hidden rounded-2xl border border-border bg-white shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left active:bg-sand-50"
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
      >
        <span className="flex items-center gap-2 font-heading text-base font-bold text-ink-900">
          {title}
          {badge}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-clay" /> : <ChevronDown className="h-4 w-4 text-clay" />}
      </button>
      {open && <div className="border-t border-border px-5 pb-5 pt-3">{children}</div>}
    </section>
  );
}

function NumField({ label, value, onChange, testId, step = 1 }) {
  return (
    <label className="block">
      <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.16em] text-ink-700">{label}</span>
      <input
        data-testid={testId}
        type="number"
        inputMode="decimal"
        step={step}
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="mt-1.5 block w-full rounded-lg border border-border bg-white px-2.5 py-2 text-right font-mono text-sm text-ink-900 outline-none focus:border-clay focus:ring-1 focus:ring-clay/30"
      />
    </label>
  );
}

export default function MobileGenerator(props) {
  const {
    schoolInfo, vendorInfo, months, rawMonths,
    activeMonthIndex, setActiveMonthIndex,
    handleQuarterChange, updateMeal, updateFund,
    totals, monthlySummaries, ingredientWiseReport, warnings,
    outsideHint,
    onPdf, onExcel, onWord, onPrint, isExporting,
    setActiveReport, activeReport, reportButtons,
    bootstrapFromMdcfs,
    activeSchool,
  } = props;

  const [ingExpanded, setIngExpanded] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  const monthName = months[activeMonthIndex]?.monthName || "";
  const rawActive = rawMonths[activeMonthIndex] || {};
  const fundActive = rawActive.funds || {};

  const validationCount = warnings?.length || 0;
  const remaining = totals?.remainingBalance ?? 0;
  const remainingNegative = remaining < 0;

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const yearInt = parseInt(String(schoolInfo.financialYear || "").split("-")[0], 10) || new Date().getFullYear();
      // Backend expects quarter as int 1..4. schoolInfo.quarter is "Q1"..."Q4".
      const qInt = parseInt(String(schoolInfo.quarter || "").replace(/^Q/i, ""), 10) || 1;
      await generatorStateApi.upsert({
        schoolId: activeSchool?.id,
        year: yearInt,
        quarter: qInt,
        data: { schoolInfo, vendorInfo, months: rawMonths },
      });
      toast.success("Draft saved");
    } catch (err) {
      console.error(err);
      toast.error("Save failed");
    } finally {
      setSavingDraft(false);
    }
  };

  const fundRow = (key, label) => {
    const f = fundActive[key] || {};
    const open = Number(f.opening || 0);
    const recv = Number(f.received || 0);
    const exp = Number(f.expenditure || 0);
    const close = open + recv - exp;
    const negative = close < 0;
    return (
      <div key={key} className="rounded-xl border border-border bg-sand-50 p-3" data-testid={`m-fund-${key}`}>
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-bold text-ink-900">{label}</p>
          <p className={`font-heading text-base font-bold ${negative ? "text-destructive" : "text-clay"}`}>{money(close)}</p>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <NumField label="Open"  value={open} onChange={(v) => updateFund(activeMonthIndex, key, "opening", v)} testId={`m-fund-${key}-open`} step="0.01" />
          <NumField label="Recv"  value={recv} onChange={(v) => updateFund(activeMonthIndex, key, "received", v)} testId={`m-fund-${key}-recv`} step="0.01" />
          <NumField label="Exp"   value={exp}  onChange={(v) => updateFund(activeMonthIndex, key, "expenditure", v)} testId={`m-fund-${key}-exp`} step="0.01" />
        </div>
      </div>
    );
  };

  return (
    <div className="md:hidden" data-testid="mobile-generator">
      {/* School identity card */}
      <section className="mx-4 mt-3 rounded-2xl border border-clay/20 bg-white p-5 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-gen-id-card">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-heading text-xl font-bold text-clay">Quarterly UC Generator</p>
            <p className="mt-1 truncate text-sm font-semibold text-ink-900" title={activeSchool?.schoolName}>
              {activeSchool?.schoolName || schoolInfo.schoolName || "—"}
            </p>
            <p className="text-xs text-ink-700">UDISE: {activeSchool?.udiseCode || schoolInfo.udiseCode || "—"}</p>
          </div>
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-clay/10 text-clay">
            <Pencil className="h-4 w-4" />
          </span>
        </div>

        <div className="mt-4 flex items-end gap-2">
          <label className="min-w-0 flex-1">
            <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Select Quarter</span>
            <Select value={schoolInfo.quarter} onValueChange={handleQuarterChange}>
              <SelectTrigger data-testid="m-quarter-trigger" className="mt-1 h-11 border-border bg-white text-sm font-semibold">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(QUARTERS).map(([k, v]) => (
                  <SelectItem key={k} value={k} data-testid={`m-quarter-${k}`}>{k} ({v.label}) · FY {schoolInfo.financialYear}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <Button
            onClick={handleSaveDraft}
            disabled={savingDraft}
            data-testid="m-save-draft-btn"
            className="h-11 shrink-0 gap-1.5 bg-clay px-3.5 text-xs font-bold text-white hover:bg-clay-dark"
          >
            <Save className="h-3.5 w-3.5" /> {savingDraft ? "Saving…" : "Save Draft"}
          </Button>
        </div>
      </section>

      {/* Missing-data warning */}
      {outsideHint && (
        <div className="mx-4 mt-3 flex items-start gap-2 rounded-xl border border-clay bg-clay-50 px-3 py-2.5 text-xs text-clay-dark" data-testid="m-outside-hint">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Data entry for <b>{QUARTERS[outsideHint.q]?.label}</b> is missing.
            Switch to <b>{outsideHint.q}</b> to fill data before generating the report.
          </span>
        </div>
      )}

      {/* Remaining Balance hero */}
      <section className="mx-4 mt-4 rounded-2xl border border-border bg-white p-5 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-gen-balance-hero">
        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-ink-700">Remaining Balance</p>
        <p className={`mt-2 border-b-2 pb-1 font-heading text-4xl font-extrabold tabular-nums ${remainingNegative ? "border-destructive text-destructive" : "border-clay text-clay"}`}>
          {money(remaining)}
        </p>
      </section>

      {/* Two stat cards */}
      <section className="mx-4 mt-3 flex gap-3">
        <Stat label="Total Cost" value={money(totals?.totalCookingCost || 0)} valueClass="text-ink-900" />
        <Stat label="Meals Served" value={qty(totals?.totalMealsServed || 0)} valueClass="text-ink-900" />
      </section>

      {/* Monthly PM POSHAN Data */}
      <Accordion title="Monthly PM POSHAN Data" defaultOpen testId="m-monthly-data">
        {/* Month tabs */}
        <div className="-mx-1 flex gap-2 overflow-x-auto pb-2">
          {months.map((m, i) => {
            const isActive = i === activeMonthIndex;
            return (
              <button
                key={`${m.monthName}-${i}`}
                onClick={() => setActiveMonthIndex(i)}
                data-testid={`m-month-tab-${i}`}
                className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition-all ${
                  isActive
                    ? "bg-clay text-white shadow-[0_6px_14px_-6px_rgb(var(--clay)/0.5)]"
                    : "bg-sand-50 text-ink-700 active:bg-sand-100"
                }`}
              >
                {m.monthName}
              </button>
            );
          })}
        </div>

        {/* A. Meal Details */}
        <p className="mt-4 flex items-center gap-2 text-sm font-bold text-ink-900">
          <span className="h-4 w-1 rounded-full bg-clay" />
          A. Meal Details — {monthName}
        </p>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <NumField label="Bal Vatika Enrolment"  value={rawActive.meals?.balVatikaEnrollment}     onChange={(v) => updateMeal(activeMonthIndex, "balVatikaEnrollment", v)}     testId="m-meal-bv" />
          <NumField label="Primary Enrolment"     value={rawActive.meals?.primaryEnrollment}       onChange={(v) => updateMeal(activeMonthIndex, "primaryEnrollment", v)}      testId="m-meal-pri" />
          <NumField label="Upper Pri. Enrolment"  value={rawActive.meals?.upperPrimaryEnrollment}  onChange={(v) => updateMeal(activeMonthIndex, "upperPrimaryEnrollment", v)} testId="m-meal-up" />
          <NumField label="Total Meals"           value={rawActive.meals?.totalMealsServed}        onChange={(v) => updateMeal(activeMonthIndex, "totalMealsServed", v)}       testId="m-meal-total" />
          <NumField label="Working Days"          value={rawActive.meals?.workingDays}             onChange={(v) => updateMeal(activeMonthIndex, "workingDays", v)}            testId="m-meal-wd" />
          <NumField label="MDM Served Days"       value={rawActive.meals?.mdmServedDays}           onChange={(v) => updateMeal(activeMonthIndex, "mdmServedDays", v)}          testId="m-meal-sd" />
        </div>

        {/* B. Fund Details */}
        <p className="mt-5 flex items-center gap-2 text-sm font-bold text-ink-900">
          <span className="h-4 w-1 rounded-full bg-clay" />
          B. Fund Details — Cooking Cost
        </p>
        <div className="mt-2 space-y-2">
          {fundRow("balVatika", "Bal Vatika")}
          {fundRow("primary", "Primary")}
          {fundRow("upperPrimary", "Upper Primary")}
        </div>
      </Accordion>

      {/* Ingredient Expenditure */}
      <Accordion title="Ingredient Expenditure" testId="m-ing-expenditure">
        {(ingredientWiseReport || []).length === 0 ? (
          <p className="rounded-lg bg-sand-50 px-3 py-2 text-xs text-ink-700">No ingredient data yet for this quarter.</p>
        ) : (
          <>
            <ul className="space-y-2">
              {(ingExpanded ? ingredientWiseReport : ingredientWiseReport.slice(0, 3)).map((row) => (
                <li key={row.name} className="flex items-start justify-between gap-2 border-b border-dashed border-border pb-2" data-testid={`m-ing-row-${row.name}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-ink-900">{row.name.replace(/_/g, " ")}</p>
                    <p className="text-[0.7rem] text-ink-700">
                      {qty(row.totalQty)} {row.unit} <span className="mx-1 text-ink-500">|</span>
                      Avg: <b className="text-ink-900">{money((Number(row.totalAmount) || 0) / Math.max(1, Number(row.totalQty) || 1))}</b>
                    </p>
                  </div>
                  <p className={`shrink-0 font-heading text-sm font-bold ${(row.totalAmount || 0) === 0 ? "text-clay" : "text-ink-900"}`}>
                    {money(row.totalAmount)}
                  </p>
                </li>
              ))}
            </ul>
            {ingredientWiseReport.length > 3 && (
              <button
                type="button"
                onClick={() => setIngExpanded((v) => !v)}
                data-testid="m-ing-view-all"
                className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full border border-clay/40 bg-white px-4 py-2 text-xs font-bold text-clay"
              >
                {ingExpanded ? "Show less" : `View All ${ingredientWiseReport.length} Items`}
              </button>
            )}
          </>
        )}
      </Accordion>

      {/* Validation */}
      {validationCount > 0 && (
        <section className="mx-4 mt-4 rounded-2xl border border-clay/30 bg-clay-50/50 p-5" data-testid="m-validation">
          <p className="flex items-center gap-2 font-heading text-sm font-bold text-clay-dark">
            <span className="grid h-6 w-6 place-items-center rounded-full bg-clay/15 text-clay">
              <ListChecks className="h-3.5 w-3.5" />
            </span>
            Validation ({validationCount})
          </p>
          <ul className="mt-3 space-y-2">
            {warnings.slice(0, 6).map((w, i) => (
              <li key={i} className="flex items-start gap-2 rounded-xl border-l-4 border-clay bg-white px-3 py-2 text-xs text-ink-900" data-testid={`m-validation-${i}`}>
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-clay" />
                <span>{w}</span>
              </li>
            ))}
            {warnings.length > 6 && (
              <li className="text-[0.7rem] text-ink-700">+{warnings.length - 6} more — open on desktop for full list.</li>
            )}
          </ul>
        </section>
      )}

      {/* Preview & Export */}
      <section className="mx-4 mt-4 rounded-2xl border border-border bg-white p-5 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="m-preview-export">
        <p className="font-heading text-xl font-bold text-ink-900">Preview &amp; Export</p>

        {/* Thumbnail-style placeholder */}
        <div className="mt-3 rounded-2xl bg-sand-100 p-4">
          <div className="rounded-xl border border-border bg-white shadow-sm">
            <div className="h-3 rounded-t-xl bg-clay" />
            <div className="space-y-1.5 p-3">
              <div className="h-2 w-1/3 rounded-full bg-sand-200" />
              <div className="grid grid-cols-6 gap-1">
                <div className="col-span-2 space-y-1">
                  <div className="h-1.5 rounded-full bg-sand-200" />
                  <div className="h-1.5 rounded-full bg-sand-200" />
                  <div className="h-1.5 rounded-full bg-sand-200" />
                </div>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="space-y-1">
                    <div className="h-1.5 rounded-full bg-sand-200" />
                    <div className="h-1.5 rounded-full bg-sand-200" />
                    <div className="h-1.5 rounded-full bg-sand-200" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="mt-2 text-center text-[0.65rem] font-semibold text-ink-700">Quarterly Financial Summary preview</p>
        </div>

        {/* Report selector chips — pick which report the preview should render. */}
        {Array.isArray(reportButtons) && reportButtons.length > 0 && (
          <div className="mt-4">
            <p className="text-[0.6rem] font-semibold uppercase tracking-[0.22em] text-ink-700">Select Report</p>
            <div className="mt-2 -mx-1 flex gap-2 overflow-x-auto pb-1" data-testid="m-report-chips">
              {reportButtons.map((r) => {
                const isActive = activeReport === r.key;
                return (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setActiveReport(r.key)}
                    data-testid={`m-tab-report-${r.key}`}
                    className={`shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all ${
                      isActive
                        ? "border-clay bg-clay text-white shadow-[0_6px_14px_-6px_rgb(var(--clay)/0.5)]"
                        : "border-border bg-white text-ink-700 active:bg-sand-50"
                    }`}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Print + PDF + Excel + Word row */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button
            onClick={onPrint}
            data-testid="m-export-print"
            className="gap-2 rounded-2xl bg-ink-900 px-4 py-3 text-sm font-bold text-white hover:bg-ink-700"
          >
            <Printer className="h-4 w-4" /> Print
          </Button>
          <Button
            onClick={onPdf}
            disabled={isExporting}
            data-testid="m-export-pdf"
            className="gap-2 rounded-2xl bg-clay px-4 py-3 text-sm font-bold text-white hover:bg-clay-dark disabled:opacity-50"
          >
            <FileDown className="h-4 w-4" /> {isExporting ? "Preparing…" : "PDF"}
          </Button>
          <Button
            onClick={onExcel}
            data-testid="m-export-excel"
            className="gap-2 rounded-2xl bg-moss px-4 py-3 text-sm font-bold text-white hover:bg-moss-dark"
          >
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </Button>
          <Button
            onClick={onWord}
            data-testid="m-export-word"
            variant="outline"
            className="gap-2 rounded-2xl px-4 py-3 text-sm font-bold"
          >
            <FileText className="h-4 w-4" /> Word
          </Button>
          <Button
            onClick={() => setActiveReport("all")}
            data-testid="m-export-all"
            variant="outline"
            className="col-span-2 gap-2 rounded-2xl px-4 py-3 text-sm font-bold"
          >
            <Sparkles className="h-4 w-4" /> Generate All Reports
          </Button>
        </div>

        {/* Bootstrap helper (kept compact, optional) */}
        {bootstrapFromMdcfs && (
          <button
            type="button"
            onClick={bootstrapFromMdcfs}
            data-testid="m-bootstrap-mdcfs"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-moss/40 bg-white px-4 py-2 text-xs font-bold text-moss-dark"
          >
            <Sparkles className="h-3.5 w-3.5" /> Bootstrap from MDCFs
          </button>
        )}
      </section>

      {/* Last updated note (small footer) */}
      <p className="mx-4 mt-4 text-[0.65rem] text-ink-700">
        <FileText className="mr-1 inline h-3 w-3 align-[-2px]" />
        {(monthlySummaries || []).length} months in this quarter · {QUARTERS[schoolInfo.quarter]?.label || ""}
      </p>

      {/* Spacer so the sticky CTA doesn't cover the footer note. Bottom tab bar
          is ~60-64px and the CTA sits at bottom-[72px] → reserve ~176px. */}
      <div className="h-44" aria-hidden />

      {/* iter146 — Sticky Generate CTA via shared StickyActionBar. */}
      <StickyActionBar
        testId="m-generate-report-btn"
        onClick={() => {
          setActiveReport("all");
          toast.success("Generating all reports — scroll down to preview");
          setTimeout(() => {
            const el = document.getElementById("report-output");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 250);
        }}
      >
        <Sparkles className="h-4 w-4" /> Generate Report
      </StickyActionBar>
    </div>
  );
}
